import { Autocomplete, TextField, SxProps, Chip, Box } from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { useForm } from '@carcareers/contexts';

export interface FreeSoloOption {
  label: string;
  value: string;
}

export interface FreeSoloInputProps {
  label: string;
  name: string;
  suggestions?: any[];
  required?: boolean;
  fullWidth?: boolean;
  multiple?: boolean;
  value?: string | string[] | null;
  autoComplete?: string;
  disabled?: boolean;
  sx?: SxProps;
  disableCloseOnSelect?: boolean;
  getOptionLabel?: (option: any) => string;
  getOptionValue?: (option: any) => string;
  groupBy?: (option: any) => string;
}

export const FreeSoloInput: FC<FreeSoloInputProps> = ({
  label,
  name,
  required,
  fullWidth,
  autoComplete,
  disabled,
  sx,
  value: propValue,
  multiple = false,
  suggestions = [],
  disableCloseOnSelect = false,
  getOptionLabel: customGetOptionLabel,
  getOptionValue: customGetOptionValue,
  groupBy: customGroupBy,
}) => {
  const { setData, getErrors } = useForm();
  const [value, setValue] = useState<string | string[] | null>(propValue ?? (multiple ? [] : ''));

  const truncateLabel = (label: string) => (label.length > 42 ? `${label.substring(0, 42)}...` : label);

  const defaultGetOptionLabel = (option: any) => {
    if (typeof option === 'string') {
      return truncateLabel(option);
    }
    return truncateLabel(option.label || option.value || '');
  };

  const extractValue = (item: any): string => {
    if (typeof item === 'string') return item;
    if (customGetOptionValue) return customGetOptionValue(item);
    return item.value || item.label || item;
  };

  const handleChange = (_: React.SyntheticEvent, newValue: any) => {
    if (Array.isArray(newValue)) {
      const values = [...new Set(newValue.map(extractValue))];
      setValue(values);
      setData(name, values);
    } else if (newValue) {
      const val = extractValue(newValue);
      setValue(val);
      setData(name, val);
    } else {
      setValue(null);
      setData(name, null);
    }
  };

  useEffect(() => {
    setValue(propValue ?? (multiple ? [] : ''));
  }, [propValue, multiple]);

  return (
    <Autocomplete
      freeSolo
      disableCloseOnSelect={disableCloseOnSelect}
      options={suggestions}
      value={value}
      multiple={multiple}
      fullWidth={fullWidth}
      onChange={handleChange}
      disabled={disabled}
      getOptionLabel={customGetOptionLabel || defaultGetOptionLabel}
      isOptionEqualToValue={(option, val) => extractValue(option) === extractValue(val)}
      groupBy={customGroupBy}
      sx={sx}
      renderOption={
        multiple
          ? (props, option) => {
              const { key, ...rest } = props as any;
              const optLabel = (customGetOptionLabel || defaultGetOptionLabel)(option);
              const optValue = extractValue(option);
              const isSelected = Array.isArray(value) && value.some((v) => extractValue(v) === optValue);
              return (
                <Box
                  key={key}
                  component="li"
                  {...rest}
                  sx={{
                    '&.MuiAutocomplete-option': {
                      bgcolor: isSelected ? 'primary.main' : undefined,
                    },
                  }}
                >
                  {optLabel}
                </Box>
              );
            }
          : undefined
      }
      renderTags={
        multiple
          ? (tagValue, getTagProps) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 0.5 }}>
                {tagValue.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  const optLabel = (customGetOptionLabel || defaultGetOptionLabel)(option);
                  return <Chip key={key} label={optLabel} size="small" {...tagProps} />;
                })}
              </Box>
            )
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          autoComplete={autoComplete}
          error={!!getErrors(name)}
          helperText={getErrors(name)}
        />
      )}
    />
  );
};
